import React from "react";
import { useParams } from "react-router-dom";

// Locals
import Loading from "../../components/loading";
import { firestoreTimestampToFormatted } from "../../helpers/format";
import { firestoreQuery } from "../../constants";

// Material-ui
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import InfoIcon from "@material-ui/icons/Info";
import BusinessIcon from "@material-ui/icons/Business";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import StarIcon from "@material-ui/icons/Star";

// Redux / Firebase / Routes / Types
import {
  useCurrentWsDateFormat,
  useCurrentWsDetail,
  useCurrentWsTier,
  useLoading,
} from "../../firebase";
import type { WsParams } from "../../routes";
import DateFormatOptionSelect from "./dateFormatOptionSelect";
import useStyles from "../styles";

const BasicInfo: React.FC = () => {
  // Workspace ID from URL
  const { workspaceId } = useParams<WsParams>();
  const dateFormat = useCurrentWsDateFormat();
  // Workspace data
  const { name, createdAt } = useCurrentWsDetail();
  const loading = useLoading(firestoreQuery.currentWsDetail);
  const tier = useCurrentWsTier();

  const classes = useStyles();

  if (loading) return <Loading />;

  const getTierColor = (tier: string): "default" | "primary" | "secondary" => {
    return "default";
  };

  return (
    <>
      <Typography variant="h6" className={classes.cardTitle} style={{ marginBottom: 20 }}>
        Workspace Information
      </Typography>

      <Box className={classes.enhancedInfoRow}>
        <InfoIcon className={classes.infoIcon} />
        <Box className={classes.infoContent}>
          <Typography className={classes.infoLabel}>ID</Typography>
          <Typography className={classes.infoValue} data-cy="workspace-id">
            {workspaceId}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.enhancedInfoRow}>
        <BusinessIcon className={classes.infoIcon} />
        <Box className={classes.infoContent}>
          <Typography className={classes.infoLabel}>Name</Typography>
          <Typography className={classes.infoValue} data-cy="workspace-name">
            {name || "Unnamed Workspace"}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.enhancedInfoRow}>
        <CalendarTodayIcon className={classes.infoIcon} />
        <Box className={classes.infoContent}>
          <Typography className={classes.infoLabel}>Created</Typography>
          <Typography className={classes.infoValue} data-cy="workspace-created">
            {firestoreTimestampToFormatted(createdAt, dateFormat)}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.enhancedInfoRow}>
        <StarIcon className={classes.infoIcon} />
        <Box className={classes.infoContent}>
          <Typography className={classes.infoLabel}>Tier</Typography>
          <Chip
            label={tier}
            color={getTierColor(tier)}
            size="small"
            className={classes.tierChip}
            data-cy="workspace-tier"
          />
        </Box>
      </Box>

      <DateFormatOptionSelect />
    </>
  );
};

export default BasicInfo;
